import classNames from "classnames";
import React, { useRef } from "react";

export interface PiUnderlineTab<T extends string> {
  id: T;
  label: string;
  /** One quiet line after the label, so a closed tab still says what's in it. */
  summary?: string;
}

/** Id of the panel a tab controls; give it to the element with role="tabpanel". */
export const piUnderlineTabPanelId = (idPrefix: string, id: string) => `${idPrefix}-panel-${id}`;
export const piUnderlineTabId = (idPrefix: string, id: string) => `${idPrefix}-tab-${id}`;

/**
 * Text tabs on a hairline with an underline under the active one
 * (Design Studio actor-settings-layout/d-*). ← → move between tabs.
 */
export default function PiUnderlineTabs<T extends string>(props: {
  tabs: PiUnderlineTab<T>[];
  value: T;
  onChange: (id: T) => void;
  ariaLabel: string;
  idPrefix: string;
  className?: string;
}) {
  const { tabs, value, onChange, ariaLabel, idPrefix, className } = props;
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const onKeyDown = (event: React.KeyboardEvent) => {
    const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const index = tabs.findIndex((tab) => tab.id === value);
    const next = tabs[(index + step + tabs.length) % tabs.length];
    onChange(next.id);
    refs.current[next.id]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={classNames("flex flex-none gap-[32px] border-0 border-b border-solid border-creator-border", className)}
      onKeyDown={onKeyDown}
    >
      {tabs.map((tab) => {
        const active = tab.id === value;
        return (
          <button
            key={tab.id}
            ref={(element) => {
              refs.current[tab.id] = element;
            }}
            type="button"
            role="tab"
            id={piUnderlineTabId(idPrefix, tab.id)}
            aria-selected={active}
            aria-controls={piUnderlineTabPanelId(idPrefix, tab.id)}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(tab.id)}
            data-testid={`${idPrefix}-tab-${tab.id}`}
            className={classNames(
              "-mb-px flex min-w-0 cursor-pointer items-baseline gap-[8px] whitespace-nowrap border-0 border-b-2 border-solid bg-transparent px-0 py-[12px] font-[inherit] text-[14px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-creator-primary",
              active
                ? "border-creator-primary font-semibold text-creator-primary"
                : "border-transparent font-medium text-creator-text-sub hover:text-creator-text"
            )}
          >
            {tab.label}
            {tab.summary && (
              <span className="truncate text-[12px] font-normal text-creator-text-sub">{tab.summary}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
